import { useApolloClient } from '@apollo/client';
import { useCallback } from 'react';
import useAppSelector, {
  useAppDispatch,
} from '../../../app/redux/useAppSelector';
import { useAddReaction } from '../../../post/comment/reactionsMutations';
import getCurrentMarkForComment from '../../../post/comment/utils/getCurrentMarkForComment';
import {
  POST_THREADS_QUERY,
  useCreateReaction,
} from '../../../post/MagneticWall';
import { useQuill } from '../../QuillContext';
import { EditorActions } from '../../editorSlice';
import { UIActions } from '../../../ui/uiSlice';

export default function usePickReaction() {
  const dispatch = useAppDispatch();
  const addReaction = useAddReaction();
  const createReaction = useCreateReaction();
  const magneticWallFocus = useAppSelector(
    (state) => state.editor.magneticWallFocus,
  );
  const postId = useAppSelector((state) => state.editor.postId);
  const currentUser = useAppSelector((state) => state.session.user);
  const sync = useAppSelector((state) => state.editor.sync);

  const { ancestorQuill: quill } = useQuill();

  const client = useApolloClient();

  return useCallback(
    async (picked: string) => {
      if (magneticWallFocus) {
        const { data } = await client.query({
          query: POST_THREADS_QUERY,
          variables: {
            // @ts-expect-error
            postId,
          },
          fetchPolicy: 'cache-first',
        });
        const isReactionActive = data.post?.threads
          ?.find((item) => item?.id === magneticWallFocus.id)
          ?.reactions?.some(
            (reaction) =>
              reaction?.emoji === picked &&
              reaction?.author?.id === currentUser.id,
          );
        if (!isReactionActive) {
          addReaction(picked, magneticWallFocus.id);
          dispatch(UIActions.addToRecentlyUsedEmojis(picked));
          dispatch(
            EditorActions.focusComment({
              id: magneticWallFocus.id,
              isActive: true,
              hasExpandIntent: false,
            }),
          );
        }
      } else if (sync && postId && quill) {
        const { version, checksum } = sync;
        createReaction(
          postId,
          version,
          checksum,
          JSON.stringify(getCurrentMarkForComment(quill)),
          picked,
        ).then(({ data }) => {
          if (data?.createReaction?.id) {
            dispatch(
              EditorActions.focusReaction({
                id: data.createReaction.id,
                isActive: true,
                hasExpandIntent: false,
              }),
            );
          }
        });
        dispatch(UIActions.addToRecentlyUsedEmojis(picked));
      }
    },
    [
      postId,
      magneticWallFocus,
      addReaction,
      client,
      dispatch,
      createReaction,
      currentUser.id,
      quill,
      sync,
    ],
  );
}
